import type { Logger } from '@mimir/common/types/logger';

export interface ReadonlyUint8Array {
  readonly [index: number]: number;
  readonly length: number;
}

export interface ReadonlyInt16Array {
  readonly [index: number]: number;
  readonly length: number;
}

export interface ReadonlyFloat32Array {
  readonly [index: number]: number;
  readonly length: number;
}


export interface RefCountedBuffer {
  copyTo(bytes: Uint8Array): Promise<void>;
  addReference(): RefCountedBuffer;
  close(): void;
  size(): number;
  asReadonlyUint8Array(): ReadonlyUint8Array,
  asReadonlyInt16Array(): ReadonlyInt16Array,
  asReadonlyFloat32Array(): ReadonlyFloat32Array,
}

export interface BufferCacheStats {
  created: number,
  free: number,
}

export interface BufferCache {
  allocateBuffer(size: number, mutate: (buffer: Uint8Array) => void): RefCountedBuffer;
  stats(): BufferCacheStats;
}

export function createBufferCache(logger: Logger): BufferCache {
  return createBufferCacheInternal(logger);
}

// ---

function createBufferCacheInternal(logger: Logger): BufferCache {
  const m : Record<string, Uint8Array[]> = { };

  const stats : BufferCacheStats = {
    created: 0,
    free: 0,
  };

  return {
    allocateBuffer(size: number, mutate: (buffer: Uint8Array) => void): RefCountedBuffer {
      m[size] ??= [];
      const cache = m[size]
      let [buffer] = cache.splice(0, 1);
      if (buffer === undefined) {
        stats.created++;
        if (stats.created > 5000) {
          logger.warn(`Creating buffer of size: ${size} bytes (created=${stats.created})`);
        }
        buffer = new Uint8Array(size);
      } else {
        stats.free --;
      }
      mutate(buffer);
      return createBuffer({ refCount: 1, buffer }, cache, stats);
    },
    stats() {
      return { ...stats };
    }
  }
}

function createBuffer(state: RefCountState, cache: Uint8Array[], stats: BufferCacheStats): RefCountedBuffer {
  let localBuffer : Uint8Array | null = state.buffer;

  const getLocalBuffer = (): Uint8Array => {
    if (localBuffer === null) {
      throw new Error('RefCountedBuffer is closed.');
    }
    return localBuffer;
  }

  return {
    async copyTo(target: Uint8Array): Promise<void> {
      target.set(getLocalBuffer());
    },
    addReference(): RefCountedBuffer {
      getLocalBuffer();
      state.refCount ++;
      return createBuffer(state, cache, stats);
    },
    close(): void {
      getLocalBuffer();
      state.refCount --;
      if (state.refCount === 0) {
        stats.free ++;
        cache.push(state.buffer);
      }
      localBuffer = null;
    },
    size(): number {
      return getLocalBuffer().length;
    },
    asReadonlyUint8Array(): ReadonlyFloat32Array {
      return new Uint8Array(getLocalBuffer().buffer);
    },
    asReadonlyInt16Array(): ReadonlyInt16Array {
      return new Int16Array(getLocalBuffer().buffer);
    },
    asReadonlyFloat32Array(): ReadonlyFloat32Array {
      return new Float32Array(getLocalBuffer().buffer);
    },
  }
}

interface RefCountState {
  refCount: number,
  buffer: Uint8Array,
}
